import { Delete } from "@mui/icons-material";
import {
	Autocomplete,
	Button,
	Checkbox,
	Chip,
	createFilterOptions,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listAllUsers } from "../../actions/form";
import {
	addOrganizationMember,
	listMemberCandidates,
	MemberCandidate,
	Organization,
	removeOrganizationMember,
} from "../../api/organizations";
import { ESRState } from "../../types/data";

// Basic shape check; the backend does full validation
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A selectable entry: an enabled TREC user, or an email to invite (no account yet). */
type MemberOption = MemberCandidate & { invite?: boolean };
const defaultFilter = createFilterOptions<MemberOption>();

type Props = {
	organization: Organization | null;
	onClose: () => void;
	onChanged: (org: Organization) => void;
};

/**
 * Lists an organization's admins and members. Users who can manage the organization
 * (site admins and organization admins) can add/remove members and grant/revoke admin.
 */
export const OrganizationMembersDialog: FC<Props> = ({
	organization,
	onClose,
	onChanged,
}) => {
	const canManage = organization?.can_manage ?? false;
	const [allUsers, setAllUsers] = useState<MemberCandidate[]>([]);
	const [newMember, setNewMember] = useState<MemberOption | null>(null);
	const [newMemberIsAdmin, setNewMemberIsAdmin] = useState(false);
	const [busy, setBusy] = useState(false);
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	// Same user lookup used elsewhere (e.g. ESRProjectSummary) to show "Name <email>"
	const user_list = useSelector((state: ESRState) => state.user_list);
	const userLabel = (email: string) => {
		const userInfo = user_list[email];
		return userInfo
			? `${userInfo.first_name} ${userInfo.last_name} <${email}>`
			: email;
	};

	useEffect(() => {
		if (!organization) return;
		setNewMember(null);
		setNewMemberIsAdmin(false);
		if (Object.keys(user_list).length === 0) {
			dispatch(listAllUsers());
		}
		if (canManage && allUsers.length === 0) {
			listMemberCandidates()
				.then(setAllUsers)
				.catch((e) => console.error(e));
		}
	}, [organization?.id]);

	if (!organization) return null;

	// Sorted by display name only, so rows don't jump around when admin is toggled
	const members = [...organization.members].sort((a, b) =>
		userLabel(a).localeCompare(userLabel(b)),
	);
	const candidates: MemberOption[] = allUsers.filter(
		(u) => !organization.members.includes(u.email),
	);
	// Offer to invite a typed email that isn't an existing TREC user or member
	const filterOptions = (
		options: MemberOption[],
		state: Parameters<typeof defaultFilter>[1],
	) => {
		const filtered = defaultFilter(options, state);
		const typed = state.inputValue.trim().toLowerCase();
		if (
			EMAIL_PATTERN.test(typed) &&
			!organization.members.includes(typed) &&
			!allUsers.some((u) => u.email.toLowerCase() === typed)
		) {
			filtered.push({ email: typed, name: "", invite: true });
		}
		return filtered;
	};

	const run = async (action: () => Promise<Organization>) => {
		setBusy(true);
		try {
			onChanged(await action());
		} catch (e) {
			// request interceptor already surfaces the error message
			console.error(e);
		} finally {
			setBusy(false);
		}
	};

	const handleAdd = () =>
		newMember &&
		run(async () => {
			const org = await addOrganizationMember(
				organization.id,
				newMember.email,
				newMemberIsAdmin,
			);
			if (org.invitation_sent) {
				enqueueSnackbar({
					variant: "success",
					message: `Invitation sent to ${newMember.email}`,
				});
			}
			setNewMember(null);
			setNewMemberIsAdmin(false);
			return org;
		});

	const renderUser = (email: string, isAdmin: boolean) => {
		const {
			title = "",
			phone = "",
			invited = false,
		} = organization.member_details?.[email] ?? {};
		const contact = [title, phone].filter(Boolean).join(" · ");
		return (
			<TableRow key={email}>
				<TableCell sx={{ wordBreak: "break-word" }}>
					{userLabel(email)}
					{invited && (
						<Tooltip title="No TREC account yet. They'll already be a member when they sign up with this email.">
							<Chip label="Invited" size="small" sx={{ ml: "8px" }} />
						</Tooltip>
					)}
					{contact && (
						<Typography fontSize={12} color="gray">
							{contact}
						</Typography>
					)}
				</TableCell>
				<TableCell padding="checkbox" align="center">
					<Checkbox
						checked={isAdmin}
						disabled={!canManage || busy}
						onChange={(e) =>
							run(() =>
								e.target.checked
									? addOrganizationMember(organization.id, email, true)
									: removeOrganizationMember(organization.id, email, true),
							)
						}
					/>
				</TableCell>
				{canManage && (
					<TableCell padding="checkbox" align="center">
						<Tooltip title="Remove from organization">
							<span>
								<IconButton
									disabled={busy}
									onClick={() =>
										run(() => removeOrganizationMember(organization.id, email))
									}
								>
									<Delete color={busy ? "disabled" : "error"} />
								</IconButton>
							</span>
						</Tooltip>
					</TableCell>
				)}
			</TableRow>
		);
	};

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{organization.name} — Members</DialogTitle>
			<DialogContent>
				{organization.members.length === 0 ? (
					<Typography color="gray">
						This organization has no members yet.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Member</TableCell>
								<TableCell align="center">Admin</TableCell>
								{canManage && <TableCell />}
							</TableRow>
						</TableHead>
						<TableBody>
							{members.map((email) =>
								renderUser(email, organization.admins.includes(email)),
							)}
						</TableBody>
					</Table>
				)}

				{canManage && (
					<Stack
						direction="row"
						alignItems="center"
						gap="12px"
						sx={{ mt: "24px" }}
					>
						<Autocomplete
							sx={{ flex: 1 }}
							options={candidates}
							value={newMember}
							onChange={(_, value) => setNewMember(value)}
							filterOptions={filterOptions}
							getOptionLabel={(u) =>
								u.invite
									? `Invite ${u.email}`
									: u.name
										? `${u.name} <${u.email}>`
										: u.email
							}
							isOptionEqualToValue={(a, b) => a.email === b.email}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									placeholder="Add a TREC user, or type an email to invite"
								/>
							)}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={newMemberIsAdmin}
									onChange={(e) => setNewMemberIsAdmin(e.target.checked)}
								/>
							}
							label="Admin"
						/>
						<Button
							variant="contained"
							disabled={!newMember || busy}
							onClick={handleAdd}
						>
							{newMember?.invite ? "Invite" : "Add"}
						</Button>
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
